import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { vesselForeground } from './useVesselTransition';
import { focusFrames, focusOptions, finishFocus } from '../lib/focusDissolve';

// For local navigation state only. Form values, loading and scroll never use this.
export default function useFocusState(initial) {
  const [value, setValue] = useState(initial);
  const current = useRef(value), requested = useRef(value), revision = useRef(0);
  const animations = useRef([]), alive = useRef(true);
  const cancel = useCallback(() => {
    animations.current.forEach(animation => animation.cancel());
    animations.current = [];
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; revision.current += 1; cancel(); };
  }, [cancel]);
  const change = useCallback(async update => {
    const next = typeof update === 'function' ? update(requested.current) : update;
    if (Object.is(next, requested.current)) return;
    requested.current = next;
    const version = ++revision.current;
    cancel();
    const targets = vesselForeground();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !targets.length || !targets.every(element => element.animate)) {
      current.current = next; setValue(next); return;
    }
    try {
      animations.current = targets.map(element => element.animate(focusFrames('exit'), focusOptions('exit')));
      await Promise.all(animations.current.map(finishFocus));
      if (!alive.current || revision.current !== version) return;
      flushSync(() => { current.current = next; setValue(next); });
      cancel();
      animations.current = vesselForeground().map(element => element.animate(focusFrames('enter'), focusOptions('enter')));
      await Promise.all(animations.current.map(finishFocus));
    } catch { /* A newer choice or unmount owns the next state. */ }
    finally { if (revision.current === version) cancel(); }
  }, [cancel]);
  const immediate = useCallback(update => {
    const next = typeof update === 'function' ? update(requested.current) : update;
    revision.current += 1; cancel(); requested.current = next; current.current = next; setValue(next);
  }, [cancel]);
  return [value, change, immediate];
}
